/**
 * IB Branding Service
 * Clean wrapper around existing WhitelabelRepository for partner branding.
 * Applies colors as CSS custom properties instead of the old store-based approach.
 */

import WhitelabelRepository from '@/repositories/whitelabel'
import store from '@/store'

class BrandingService {

  /**
   * Load partner branding by API key and apply it
   * @param {string} apiKey - The partner's whitelabel API key (VUE_APP_IB_PARTNER_KEY)
   * @param {object} vuetify - The Vuetify instance for theme updates
   */
  async loadPartnerBranding(apiKey, vuetify) {
    if (!apiKey) {
      console.warn('[IB Branding] No partner key provided, using defaults')
      this.applyDefaultBranding()
      return null
    }

    try {
      const response = await WhitelabelRepository.getWhitelabelByApiKey(apiKey)
      const data = response.data

      if (!data) {
        console.warn('[IB Branding] No partner data returned')
        this.applyDefaultBranding()
        return null
      }

      const partner = this._extractPartnerData(data)

      // Store in Vuex
      store.dispatch('ib/setPartnerBranding', partner)

      // Apply CSS custom properties
      this.applyPartnerBranding(partner, vuetify)

      // Update document title
      if (partner.name) {
        document.title = `${partner.name} - IB Space`
      }

      return partner
    } catch (error) {
      console.error('[IB Branding] Failed to load partner branding:', error)
      this.applyDefaultBranding()
      return null
    }
  }

  /**
   * Extract clean partner data from whitelabel API response
   * API shape: { whitelabel: { partner: { shortName, mainColor, partnerBrand, ... }, partnerBrandThemes: [...] } }
   */
  _extractPartnerData(data) {
    const wl = data.whitelabel || data
    const partner = wl.partner || {}
    const brand = partner.partnerBrand || {}

    // Find theme objects from partnerBrandThemes array
    const themes = wl.partnerBrandThemes || []
    const lightTheme = themes.find(t => t.type === 'LIGHT') || {}
    const darkTheme = themes.find(t => t.type === 'DARK') || {}

    // Primary color: mainBtnBackground > mainColor > fallback
    const primaryColor = this._toHex(lightTheme.mainBtnBackground || partner.mainColor)

    return {
      id: partner.id || null,
      name: partner.shortName || partner.fullName || '',
      logoUrl: partner.fullLogoUrl || brand.fullLogo || '',
      responsiveLogoUrl: brand.responsiveLogo || partner.responsiveLogoUrl || '',
      websiteUrl: partner.websiteUrl || '',
      primaryColor: primaryColor,
      themes: {
        light: {
          primary: primaryColor,
          navBg: this._toHex(lightTheme.navBarBackground) || '#0F0F0F',
          navText: this._toHex(lightTheme.mainBlack) || '#FFFFFF',
          cardBg: this._toHex(lightTheme.mainWhite) || '#FFFFFF',
          pageBg: this._toHex(lightTheme.baseBackground) || '#F8F9FA',
          accent: this._toHex(lightTheme.brandAccent) || '#6366F1'
        },
        dark: {
          primary: primaryColor,
          navBg: this._toHex(darkTheme.navBarBackground) || '#000000',
          navText: this._toHex(darkTheme.mainBlack) || '#FFFFFF',
          cardBg: this._toHex(darkTheme.mainWhite) || '#1E1E1E',
          pageBg: this._toHex(darkTheme.baseBackground) || '#121212',
          accent: this._toHex(darkTheme.brandAccent) || '#6366F1'
        }
      }
    }
  }

  /**
   * Ensure color string has # prefix, return fallback if empty
   */
  _toHex(color) {
    if (!color || color.trim() === '') return ''
    return color.startsWith('#') ? color : `#${color}`
  }

  /**
   * Apply partner branding via CSS custom properties
   */
  applyPartnerBranding(partner, vuetify) {
    const root = document.documentElement
    const theme = partner.themes?.light || {}

    root.style.setProperty('--ib-primary', partner.primaryColor || '#6366F1')
    root.style.setProperty('--ib-nav-bg', theme.navBg || '#0F0F0F')
    root.style.setProperty('--ib-nav-text', theme.navText || '#FFFFFF')
    root.style.setProperty('--ib-card-bg', theme.cardBg || '#FFFFFF')
    root.style.setProperty('--ib-page-bg', theme.pageBg || '#F8F9FA')
    root.style.setProperty('--ib-accent', theme.accent || partner.primaryColor || '#6366F1')

    // Update Vuetify theme if available
    if (vuetify && partner.primaryColor) {
      vuetify.theme.themes.light.primary = partner.primaryColor
    }
  }

  /**
   * Apply default branding when no partner key is set
   */
  applyDefaultBranding() {
    const root = document.documentElement
    root.style.setProperty('--ib-primary', '#6366F1')
    root.style.setProperty('--ib-nav-bg', '#0F0F0F')
    root.style.setProperty('--ib-nav-text', '#FFFFFF')
    root.style.setProperty('--ib-card-bg', '#FFFFFF')
    root.style.setProperty('--ib-page-bg', '#F8F9FA')
  }
}

export default new BrandingService()
